import type { OceanAgentRole, OceanBedrockModel, OceanOrgSkill } from "@vida/currents-core";

export const DEFAULT_MODEL_ID = "global.anthropic.claude-haiku-4-5-20251001-v1:0";

/** App satellite selezionabili in Assegnazioni agente (allineato a vida_allowed_app_ids). */
export const OCEAN_ASSIGNABLE_APP_IDS = [
  { id: "currents", label: "Currents (console admin)" },
  { id: "vibedashboard", label: "VibeDashboard" },
  { id: "pulse", label: "Pulse" },
  { id: "wave", label: "Wave" },
  { id: "tide", label: "Tide" },
  { id: "drift", label: "Drift" },
  { id: "still", label: "Still" },
  { id: "reef", label: "Reef" },
  { id: "surf", label: "Surf" },
  { id: "seas", label: "Seas" },
  { id: "harbor", label: "Harbor" },
] as const;

/** Fallback allineato a AppState.bedrockModels (inference profile + on-demand). */
export const BEDROCK_MODEL_FALLBACK: OceanBedrockModel[] = [
  { modelId: "eu.amazon.nova-micro-v1:0", label: "Amazon Nova Micro (EU)", providerName: "Amazon", sortOrder: 0, enabled: true, updatedAt: "" },
  { modelId: "eu.amazon.nova-lite-v1:0", label: "Amazon Nova Lite (EU)", providerName: "Amazon", sortOrder: 1, enabled: true, updatedAt: "" },
  { modelId: "eu.amazon.nova-pro-v1:0", label: "Amazon Nova Pro (EU)", providerName: "Amazon", sortOrder: 2, enabled: true, updatedAt: "" },
  { modelId: "global.anthropic.claude-sonnet-4-6", label: "Claude Sonnet 4.6 (Global)", providerName: "Anthropic", sortOrder: 6, enabled: true, updatedAt: "" },
  { modelId: "global.anthropic.claude-haiku-4-5-20251001-v1:0", label: "Claude Haiku 4.5 (Global)", providerName: "Anthropic", sortOrder: 9, enabled: true, updatedAt: "" },
  { modelId: "global.anthropic.claude-opus-4-6-v1", label: "Claude Opus 4.6 (Global)", providerName: "Anthropic", sortOrder: 11, enabled: true, updatedAt: "" },
  { modelId: "global.amazon.nova-2-lite-v1:0", label: "Amazon Nova 2 Lite (Global)", providerName: "Amazon", sortOrder: 3, enabled: true, updatedAt: "" },
];

export const AGENT_ROLES: OceanAgentRole[] = [
  "ORCHESTRATOR",
  "CREATIVE",
  "RESEARCHER",
  "WRITER",
  "ANALYST",
  "VALIDATOR",
  "OPERATOR",
  "SECRETARY",
  "PROJECT_MANAGER",
  "CUSTOM",
];

export const ROLE_LABELS: Record<OceanAgentRole, string> = {
  ORCHESTRATOR: "Orchestratore",
  CREATIVE: "Creativo",
  RESEARCHER: "Ricercatore",
  WRITER: "Scrittore",
  ANALYST: "Analista",
  VALIDATOR: "Validatore",
  OPERATOR: "Operatore",
  SECRETARY: "Segretario",
  PROJECT_MANAGER: "Project manager",
  CUSTOM: "Personalizzato",
};

export const ROLE_ICONS: Record<OceanAgentRole, string> = {
  ORCHESTRATOR: "🎛️",
  CREATIVE: "🎨",
  RESEARCHER: "🔬",
  WRITER: "✍️",
  ANALYST: "📊",
  VALIDATOR: "✅",
  OPERATOR: "⚙️",
  SECRETARY: "📋",
  PROJECT_MANAGER: "📌",
  CUSTOM: "🤖",
};

export const PICKABLE_ICONS = [
  "🎛️",
  "🎨",
  "🔬",
  "✍️",
  "📊",
  "✅",
  "⚙️",
  "📋",
  "📌",
  "🤖",
  "🧠",
  "🌊",
  "🔍",
  "🌐",
  "📰",
  "📝",
  "🛡️",
];

export const DEFAULT_SYSTEM_PROMPT = `Sei {{name}}, un agente Oceany con ruolo {{role}}.
Obiettivo missione: {{mission.goal}}
Vincoli: cita sempre le fonti; non eseguire azioni di scrittura; rispondi in italiano salvo richiesta diversa.`;

export const ORCHESTRATOR_SYSTEM_PROMPT = `Sei {{name}}, orchestratore Oceany.
Coordina gli agenti del team elencati sotto: assegna sotto-obiettivi, sintetizza i risultati e mantieni coerenza.
Obiettivo missione: {{mission.goal}}
Non inventare capacità degli agenti oltre a identità e skill dichiarate.`;

export const CREATIVE_SYSTEM_PROMPT = `Sei {{name}}, agente creativo Oceany.
Obiettivo: produrre contenuti originali — testi, immagini, video o audio — in linea con la missione.
Missione: {{mission.goal}}

Linee guida:
- Chiarisci formato, tono, durata e vincoli di brand prima di generare.
- Testi: struttura leggibile, voce coerente; proponi varianti se utili.
- Immagini: usa le skill MODEL con parametri strutturati (subject, style, mood, background…); non inviare prompt liberi al provider.
- Video e audio: indica formato, durata e riferimenti; usa i tool model disponibili con parametri espliciti.
- Quando un tool restituisce un media, includi nel testo finale il riferimento [attachment:<id>].
- Rispondi in italiano salvo richiesta diversa; rispetta i guardrail dell'organizzazione.`;

export const ROLE_SYSTEM_PROMPTS: Partial<Record<OceanAgentRole, string>> = {
  ORCHESTRATOR: ORCHESTRATOR_SYSTEM_PROMPT,
  CREATIVE: CREATIVE_SYSTEM_PROMPT,
};

export function systemPromptForRole(role: OceanAgentRole): string {
  return ROLE_SYSTEM_PROMPTS[role] ?? DEFAULT_SYSTEM_PROMPT;
}

export function roleLabel(role: OceanAgentRole): string {
  return ROLE_LABELS[role] ?? role;
}

export function iconForRole(role: OceanAgentRole): string {
  return ROLE_ICONS[role] ?? "🤖";
}

export function toolsJsonFromSkillIds(skillIds: string[], catalog: OceanOrgSkill[]): string {
  const refs = new Set<string>();
  for (const id of skillIds) {
    const row = catalog.find((s) => s.id === id);
    if (row) {
      for (const t of row.toolRefs) refs.add(t);
    }
  }
  return JSON.stringify([...refs].map((ref) => ({ ref })));
}

export function defaultPermissionsJson(): string {
  return JSON.stringify({
    dataScopes: [
      { domain: "notes", actions: ["read"] },
      { domain: "reef", actions: ["read"] },
      { domain: "knowledge", actions: ["read"] },
    ],
    layers: ["surface", "epipelagic", "mesopelagic"],
    outbound: ["web"],
    budget: { maxCostCents: 200, maxToolCalls: 25 },
  });
}

export const RETRIEVAL_AGENT_TEMPLATE = {
  name: "Retrieval",
  role: "RESEARCHER" as const,
  icon: "📥",
  description: "Indicizza pagine web e allegati nella knowledge org",
  systemPromptTemplate: `Sei {{name}}, agente Retrieval Oceany.
Obiettivo: indicizzare contenuti web nella knowledge dell'organizzazione e rispondere con ricerche mirate.
Missione: {{mission.goal}}
Usa web.read per ispezionare URL, ingest.web_bundle per scaricare allegati e knowledge.search per verificare l'indicizzazione.
Cita sempre le fonti (URL, titolo pagina, file indicizzati).`,
  skillIds: ["web-research", "web-ingest", "semantic-search"],
  guardrailIds: ["cite-sources"],
  permissionsJson: JSON.stringify({
    dataScopes: [
      { domain: "notes", actions: ["read"] },
      { domain: "knowledge", actions: ["read", "write"] },
    ],
    outbound: ["web"],
    budget: { maxCostCents: 500, maxToolCalls: 30, maxScriptSeconds: 120 },
  }),
};

export const ILLUSTRATOR_AGENT_TEMPLATE = {
  name: "Illustrator",
  role: "CREATIVE" as const,
  icon: "🖼️",
  description: "Genera immagini prodotto da goal testuale via skill MODEL",
  systemPromptTemplate: `Sei {{name}}, agente Illustrator Oceany.
Obiettivo: creare immagini professionali usando la skill image-generate.
Missione: {{mission.goal}}
Estrai dal goal i parametri subject, style e background e invocali tramite il tool model_image-generate.
Non inventare prompt liberi per il modello: passa solo parametri strutturati.
Quando l'immagine è pronta, includi nel testo finale il riferimento [attachment:<id>] restituito dal tool.`,
  skillIds: ["image-generate", "notes-context"],
  guardrailIds: ["cite-sources"],
  permissionsJson: JSON.stringify({
    dataScopes: [{ domain: "notes", actions: ["read"] }],
    budget: { maxCostCents: 400, maxToolCalls: 15 },
  }),
};

/** Prompt di default noti (per auto-sostituzione in creazione agente). */
export function isKnownDefaultSystemPrompt(prompt: string): boolean {
  const normalized = prompt.trim();
  const known = new Set<string>([
    DEFAULT_SYSTEM_PROMPT.trim(),
    ORCHESTRATOR_SYSTEM_PROMPT.trim(),
    CREATIVE_SYSTEM_PROMPT.trim(),
    RETRIEVAL_AGENT_TEMPLATE.systemPromptTemplate.trim(),
    ILLUSTRATOR_AGENT_TEMPLATE.systemPromptTemplate.trim(),
  ]);
  return known.has(normalized);
}

export const MODEL_SAMPLE_PARAMS_PRESETS: Record<string, string> = {
  default:
    '{\n  "subject": "smartwatch su tavolo in legno",\n  "style": "minimalista premium",\n  "background": "bianco soft"\n}',
};

/** Binding iniziale per nuove skill MODEL (allineato al seed image-generate). */
export const DEFAULT_MODEL_BINDING = {
  jobKind: "IMAGE_GENERATE",
  provider: "OPENAI",
  modelId: "gpt-image-2",
  defaults: { size: "1024x1024", quality: "medium", n: 1 },
  exposeParams: [
    { name: "subject", type: "string", description: "Soggetto principale", required: true },
    { name: "style", type: "string", description: "Stile visivo", required: false },
    { name: "background", type: "string", description: "Sfondo", required: false },
  ],
  promptTemplate:
    "Professional product photograph of {{subject}}, style {{style}}, background {{background}}",
};

export const DEFAULT_MODEL_BINDING_JSON = JSON.stringify(DEFAULT_MODEL_BINDING, null, 2);

export type CreatableSkillType = "SCRIPT" | "MODEL";

export const CREATABLE_SKILL_TYPES: {
  value: CreatableSkillType;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    value: "SCRIPT",
    label: "Script Python",
    description: "Sandbox Code Interpreter: descrizione → generazione script → test → publish.",
    icon: "🧩",
  },
  {
    value: "MODEL",
    label: "Modello media",
    description: "Binding a provider (es. gpt-image-2) con parametri strutturati e prompt template.",
    icon: "🖼️",
  },
];

export const SCRIPT_SAMPLE_INPUT_PRESETS: Record<string, string> = {
  default: '{\n  "topic": "AI"\n}',
  reefDigest:
    '{\n  "topic": "machine learning",\n  "maxArticles": 5\n}',
};

export const SCRIPT_SAMPLE_DATA_PRESETS: Record<string, string> = {
  default: '{\n  "articles": []\n}',
  reefDigest:
    '{\n  "articles": [\n    {"title": "Esempio Reef", "summary": "Sintesi articolo"}\n  ]\n}',
};
